/**
 * История сообщений.
 *
 * Раскладка намеренно повторяет Telegram: пузыри с хвостом у последнего
 * сообщения в группе, слипшиеся подряд идущие сообщения одного отправителя,
 * плавающие разделители дат, время и галочки статуса, вписанные в нижний угол
 * пузыря. Менеджер, весь день работающий в Telegram, не должен переучиваться.
 *
 * Отдельно стоит отметить подпись автора у исходящих: Telegram не знает, кто из
 * десяти менеджеров написал сообщение — для него это один аккаунт. Поле
 * `sentBy` приходит из нашей БД и существует только для сообщений, отправленных
 * через систему (§29 ТЗ). У сообщений, отправленных с телефона напрямую, его
 * нет, и подставлять туда «кого-нибудь» нельзя.
 */

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  TelegramChatDto,
  TelegramMessageDto,
  TelegramPresenceDto,
  TelegramTextEntityDto,
} from "@tw/shared-types";
import { useSession } from "../state/session.tsx";
import { ApiError } from "../lib/api.js";
import { accountLabel } from "./accountLabel.js";
import { Avatar } from "./Avatar.tsx";
import { MessageMedia } from "./MessageMedia.tsx";
import { CallCard } from "./CallCard.tsx";
import { TypingDots } from "./TypingDots.tsx";
import { messageSummary } from "@/lib/message-summary.ts";
import { sameMessage, telegramIdOf } from "@/lib/message-id.ts";
import { hasCustomEmoji, writeRichClipboard } from "@/lib/rich-clipboard.ts";
import { chatTypeBadge } from "./chatType.js";
import { RichText } from "./RichText.tsx";
import { SaveAsTemplateDialog } from "./SaveAsTemplateDialog.tsx";
import {
  AlertIcon,
  BoltIcon,
  CheckIcon,
  ChecksIcon,
  ChevronIcon,
  ClockIcon,
  CopyIcon,
  PencilIcon,
  PinIcon,
  PinOffIcon,
  ReplyIcon,
  SearchIcon,
  TrashIcon,
} from "./icons.tsx";
import { chatTitle } from "./chatTitle.js";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Реакция «в один клик».
 *
 * Telegram ставит по двойному нажатию ту, что выбрана в настройках; у нас
 * настройки нет, и палец большого пальца — самый нейтральный выбор из
 * возможных. Остальные реакции ставятся из самого Telegram.
 */
/** Как часто перечитывать статус собеседника. */
const PRESENCE_INTERVAL_MS = 60_000;

const QUICK_REACTION = "👍";

/**
 * Набор реакций.
 *
 * Тот же, что Telegram предлагает первым в личных чатах. Полный список
 * аккаунта тянуть не стали: он зависит от Premium и от настроек чата, а
 * недоступная реакция возвращает ошибку уже после нажатия — хуже, чем
 * короткий предсказуемый ряд.
 */
const REACTIONS = ["👍", "👎", "❤️", "🔥", "🎉", "😁", "😢", "🙏"] as const;

interface MessageListProps {
  chat: TelegramChatDto;
  /** «печатает», «записывает голосовое» — вместо статуса в шапке. */
  activity?: string | null;
  messages: TelegramMessageDto[];
  /** Есть ли сообщения раньше первого показанного. */
  hasOlder: boolean;
  /** Ранние сообщения сейчас грузятся. */
  loadingOlder: boolean;
  /** Подгрузить ранние — лента дошла до верхнего края. */
  onLoadOlder: () => void;
  onReload: () => void;
  /** Выделенные сообщения. Пустое множество — режим выделения выключен. */
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (messageId: string) => void;
  /** Ответить на сообщение — композер подставит цитату. */
  onReply: (message: TelegramMessageDto) => void;
  /** Открыть чат по @username из ссылки в сообщении. */
  onOpenPeer: (username: string) => void;
  /** Телефон: назад к списку чатов. Есть — в шапке кнопка «Назад». */
  onBack?: () => void;
  /** Телефон: открыть карточку чата нажатием на шапку. */
  onOpenDetails?: () => void;
}

/**
 * Порог склейки сообщений в группу. Пять минут — телеграмная величина: за это
 * время собеседник воспринимается как продолжающий одну мысль, а не начинающий
 * новую.
 */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

const PRESENCE: Record<string, string> = {
  CONNECTED: "bg-[var(--ok)]",
  RECONNECTING: "bg-[var(--warn)]",
  AUTHORIZATION_REQUIRED: "bg-[var(--warn)]",
  ERROR: "bg-destructive",
  DISCONNECTED: "bg-muted-foreground",
};

export function MessageList({
  chat,
  activity,
  messages,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  onReload,
  selectedIds,
  onToggleSelect,
  onReply,
  onOpenPeer,
  onBack,
  onOpenDetails,
}: MessageListProps) {
  const selecting = selectedIds.size > 0;
  const { api, can } = useSession();
  const bottomRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  /** Статус собеседника: «в сети», «был(а) в 14:32», «12 участников». */
  const [presence, setPresence] = useState<TelegramPresenceDto | null>(null);

  /** Сообщение, для которого открыт выбор области закрепления. */
  const [pinAsk, setPinAsk] = useState<string | null>(null);

  /** Сообщение, для которого открыта палитра реакций. */
  const [reactAsk, setReactAsk] = useState<string | null>(null);

  /** Сообщение, которое сохраняют в заготовки. */
  const [templateFrom, setTemplateFrom] = useState<TelegramMessageDto | null>(null);
  const canManageTemplates = can("quickreply.manage");

  /** Подсвеченное сообщение после перехода. Гаснет само. */
  const [highlighted, setHighlighted] = useState<string | null>(null);

  /** Стоит ли лента у нижнего края. От этого зависит поведение автопрокрутки. */
  const [atBottom, setAtBottom] = useState(true);
  /** Сколько сообщений пришло, пока человек читал историю. */
  const [missed, setMissed] = useState(0);

  const seenCount = useRef(messages.length);

  /*
   * Отслеживание положения прокрутки.
   *
   * Порог в 80 пикселей, а не строгое равенство: у ленты плавная прокрутка и
   * дробные высоты, и точное совпадение scrollTop с максимумом почти никогда
   * не наступает.
   */
  /*
   * Подгрузка ранних — через ref: обработчик прокрутки вешается раз на чат,
   * а флаги меняются после каждой страницы.
   */
  const olderRef = useRef({ hasOlder, loadingOlder, onLoadOlder });
  olderRef.current = { hasOlder, loadingOlder, onLoadOlder };

  useEffect(() => {
    const node = feedRef.current;
    if (!node) return;

    const check = (): void => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
      const near = distance < 80;
      setAtBottom(near);
      if (near) setMissed(0);

      // Заранее, за пару экранов до края: подгрузка идёт по сети, и упереться
      // в пустой верх ленты, а потом ждать — хуже, чем не заметить подгрузку.
      const older = olderRef.current;
      if (node.scrollTop < 600 && older.hasOlder && !older.loadingOlder) {
        older.onLoadOlder();
      }
    };

    check();
    node.addEventListener("scroll", check, { passive: true });
    return () => node.removeEventListener("scroll", check);
  }, [chat.id]);

  /*
   * Прокрутка при открытии чата — мгновенная и в самый низ.
   *
   * Плавная здесь неуместна: при открытии переписки человек не следит за
   * движением, он ждёт последнее сообщение. Анимация от начала истории вниз
   * занимает секунды и выглядит как подвисание.
   */
  useEffect(() => {
    seenCount.current = 0;
    setMissed(0);
    setAtBottom(true);
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [chat.id]);

  /*
   * Новые сообщения.
   *
   * Прокручиваем ТОЛЬКО если человек стоял внизу. Раньше лента дёргалась вниз
   * безусловно: стоило открыть историю и начать читать, как первое же входящее
   * выбрасывало к последнему сообщению, теряя место чтения. В Telegram в этом
   * случае показывается кнопка со счётчиком, и решение остаётся за человеком.
   */
  useEffect(() => {
    // Первая порция истории открытого чата — мгновенно, как и само
    // открытие: плавная прокрутка через полсотни сообщений выглядит как
    // подвисание. Плавно — только то, что пришло потом.
    const first = seenCount.current === 0;
    const added = messages.length - seenCount.current;
    seenCount.current = messages.length;
    if (added <= 0) return;
    // Добавились ранние сверху, а не новые снизу: прокручивать и считать
    // «пропущенные» нечего.
    if (!first && prepended.current) return;

    if (atBottom) {
      bottomRef.current?.scrollIntoView({ behavior: first ? "auto" : "smooth" });
    } else {
      setMissed((v) => v + added);
    }
    // atBottom намеренно вне зависимостей: эффект должен срабатывать на приход
    // сообщений, а не на каждое движение прокрутки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  /*
   * Место чтения при подгрузке сверху.
   *
   * Браузер держит scrollTop, а не то, что на экране: без поправки добавленные
   * сверху 50 сообщений сдвинули бы ленту, и человек оказался бы в другом
   * месте переписки. Поправка — до отрисовки, иначе виден скачок.
   */
  const lastKey = messages.at(-1)?.id ?? null;
  const firstKey = messages[0]?.id ?? null;
  const layout = useRef({ first: null as string | null, last: null as string | null, height: 0 });
  /** Последнее изменение ленты — ранние сверху. Читает эффект новых сообщений. */
  const prepended = useRef(false);
  useLayoutEffect(() => {
    const node = feedRef.current;
    if (!node) return;
    const prev = layout.current;
    const grewAtTop =
      prev.first !== null && firstKey !== prev.first && lastKey === prev.last;
    if (grewAtTop) node.scrollTop += node.scrollHeight - prev.height;
    if (firstKey !== prev.first || lastKey !== prev.last) prepended.current = grewAtTop;
    layout.current = { first: firstKey, last: lastKey, height: node.scrollHeight };
  });

  /*
   * Короткая страница не даёт прокрутки — а без прокрутки подгрузку сверху
   * нечем запустить. Так выглядел холодный чат: два сообщения, и всё.
   */
  useEffect(() => {
    const node = feedRef.current;
    if (!node || !hasOlder || loadingOlder) return;
    if (node.scrollHeight <= node.clientHeight + 600) onLoadOlder();
  }, [messages.length, hasOlder, loadingOlder, onLoadOlder]);

  /**
   * Перейти к сообщению в ленте.
   *
   * Ищем узел по data-id, а не по индексу: лента перерисовывается, и индекс
   * успевает устареть между поиском и прокруткой.
   *
   * Если сообщения в загруженной истории нет — а так бывает и с закреплённым,
   * и с найденным поиском, — честно говорим об этом. Молча ничего не делать
   * здесь худший вариант: нажатие выглядит сломанным.
   */
  const jumpTo = (target: string): void => {
    // Закреплённое и найденное приходят с id вида tg:N, а в ленте то же
    // сообщение, отправленное через систему, лежит под нашим UUID.
    const messageId =
      messages.find((m) => sameMessage(m, { id: target }))?.id ?? target;
    const node = feedRef.current?.querySelector(
      `[data-message-id="${CSS.escape(messageId)}"]`,
    );

    if (!node) {
      setError(
        "Это сообщение вне загруженной части переписки — прокрутите выше",
      );
      return;
    }

    setError(null);
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlighted(messageId);
    window.setTimeout(
      () => setHighlighted((v) => (v === messageId ? null : v)),
      1600,
    );
  };

  const scrollToBottom = (): void => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    setMissed(0);
  };

  /*
   * Граница непрочитанного.
   *
   * Telegram рисует разделитель перед первым непрочитанным сообщением.
   * Считаем от конца: непрочитанными могут быть только входящие, и их ровно
   * столько, сколько говорит счётчик чата. Если счётчик больше загруженной
   * истории, разделитель не рисуем вовсе — поставить его в начало значило бы
   * заявить, что непрочитана вся переписка.
   */
  const firstUnreadId = ((): string | null => {
    if (chat.unreadCount <= 0) return null;
    const inbound = messages.filter((m) => m.direction === "INBOUND");
    if (chat.unreadCount > inbound.length) return null;
    return inbound[inbound.length - chat.unreadCount]?.id ?? null;
  })();

  const canDelete = can("telegram.message.delete");
  const canEdit = can("telegram.message.edit");

  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const saveEdit = async (message: TelegramMessageDto): Promise<void> => {
    const telegramId = telegramIdOf(message);
    const text = draft.trim();
    if (!telegramId || !text || text === (message.text ?? "")) {
      setEditing(null);
      return;
    }

    setError(null);
    try {
      await api.editMessage(chat.id, telegramId, text);
      setEditing(null);
      onReload();
    } catch (err) {
      // Telegram запрещает править чужие и слишком старые сообщения —
      // причину нужно показать, а не молча вернуть поле в прежний вид.
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Не удалось изменить сообщение",
      );
    }
  };

  const remove = async (message: TelegramMessageDto): Promise<void> => {
    const telegramId = telegramIdOf(message);
    if (!telegramId) return;

    // Удаление необратимо и видно клиенту — подтверждение обязательно.
    if (
      !window.confirm("Удалить сообщение у собеседника? Действие необратимо.")
    )
      return;

    // Отказ Telegram (чужое, слишком старое) показываем, как и при правке:
    // иначе нажатие выглядит выполненным, а сообщение остаётся у клиента.
    setError(null);
    try {
      await api.deleteMessage(chat.id, telegramId);
      onReload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось удалить сообщение",
      );
    }
  };

  const canReply = can("telegram.message.reply");
  /*
   * Закрепление и реакции идут под правом ОТПРАВКИ, а не чтения.
   *
   * Оба действия видны собеседнику: закреплённое всплывает у него под шапкой,
   * реакция появляется под сообщением. Это высказывания рабочего аккаунта
   * наружу, и стажёр с READ_ONLY их делать не должен.
   */
  const canAct = can("telegram.message.send");

  const [pinned, setPinned] = useState<TelegramMessageDto | null>(null);
  /** Все закреплённые и открыта ли панель со списком. */
  const [allPinned, setAllPinned] = useState<TelegramMessageDto[] | null>(null);
  const [pinnedListOpen, setPinnedListOpen] = useState(false);
  /** Закреплённое приходит как tg:N, а наше отправленное в ленте — под UUID. */
  const isPinned = (message: TelegramMessageDto): boolean =>
    pinned !== null && sameMessage(pinned, message);

  const loadPinned = useCallback(async () => {
    try {
      const { message } = await api.pinnedMessage(chat.id);
      setPinned(message);
    } catch {
      // Закреплённого может не быть, и это штатно, а не сбой.
      setPinned(null);
    }
  }, [api, chat.id]);

  useEffect(() => {
    void loadPinned();
    setAllPinned(null);
    setPinnedListOpen(false);
  }, [loadPinned]);

  /*
   * Все закрепы грузим по требованию: панель «Закреплённые» открывают редко,
   * а список бьёт отдельным запросом на сервер (searchChatMessages). Держать
   * его загруженным на каждый чат — лишняя нагрузка на TDLib.
   */
  const openPinnedList = useCallback(async () => {
    setPinnedListOpen(true);
    if (allPinned) return;
    try {
      const { items } = await api.pinnedMessages(chat.id);
      setAllPinned(items);
    } catch {
      setAllPinned([]);
    }
  }, [api, chat.id, allPinned]);

  /*
   * Статус собеседника обновляем по таймеру.
   *
   * «В сети» живёт минуты, и однажды прочитанный статус быстро превращается в
   * ложь: подпись «в сети» под чатом, из которого человек ушёл полчаса назад,
   * хуже, чем её отсутствие. Минута — компромисс между свежестью и числом
   * обращений к Telegram, который считает лимиты на аккаунт.
   */
  useEffect(() => {
    let alive = true;

    const load = async (): Promise<void> => {
      try {
        const result = await api.chatPresence(chat.id);
        if (alive) setPresence(result.presence);
      } catch {
        if (alive) setPresence(null);
      }
    };

    setPresence(null);
    void load();
    const timer = window.setInterval(() => void load(), PRESENCE_INTERVAL_MS);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [api, chat.id]);

  /*
   * `unpin` задаётся явно из панели «Закреплённые»: там любое сообщение
   * закреплено, но сверка идёт лишь с верхним, и не верхнее вместо
   * открепления закреплялось повторно.
   */
  const togglePin = async (
    message: TelegramMessageDto,
    onlyForSelf = false,
    unpin = isPinned(message),
  ): Promise<void> => {
    setError(null);
    setPinAsk(null);
    const telegramId = telegramIdOf(message);
    if (!telegramId) return;
    try {
      if (unpin) {
        await api.unpinMessage(chat.id, telegramId);
      } else {
        await api.pinMessage(chat.id, telegramId, onlyForSelf);
      }
      await loadPinned();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось изменить закрепление",
      );
    }
  };

  const toggleReaction = async (
    message: TelegramMessageDto,
    emoji: string,
  ): Promise<void> => {
    setError(null);
    setReactAsk(null);
    const mine = (message.reactions ?? []).find(
      (r) => r.emoji === emoji && r.chosen,
    );
    const telegramId = telegramIdOf(message);
    if (!telegramId) return;
    try {
      if (mine) await api.removeReaction(chat.id, telegramId, emoji);
      else await api.addReaction(chat.id, telegramId, emoji);
      // Перечитываем ленту: реакция приходит из TDLib, и собрать её состояние
      // на клиенте значило бы держать вторую версию правды.
      onReload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Telegram не принял реакцию",
      );
    }
  };

  /*
   * Поиск по переписке.
   *
   * Серверный, а не по загруженным сообщениям: в ленте лежит последняя
   * полусотня, и поиск по ней находил бы только то, что и так на виду.
   * Telegram ищет по всей истории, и половинчатая замена вводила бы в
   * заблуждение хуже, чем её отсутствие.
   */
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<TelegramMessageDto[] | null>(null);
  const [searching, setSearching] = useState(false);

  const runSearch = async (): Promise<void> => {
    const value = query.trim();
    if (value.length === 0) return;
    setSearching(true);
    setError(null);
    try {
      const { items } = await api.searchInChat(chat.id, { q: value });
      setFound(items);
    } catch (err) {
      setFound([]);
      setError(err instanceof Error ? err.message : "Поиск не удался");
    } finally {
      setSearching(false);
    }
  };

  const closeSearch = (): void => {
    setSearchOpen(false);
    setQuery("");
    setFound(null);
  };

  /*
   * Смена чата закрывает поиск: результаты принадлежали покинутой переписке.
   *
   * Состояние сбрасывается прямо здесь, а не вызовом closeSearch: замыкание
   * на функцию потребовало бы её в зависимостях, и подавлять правило пришлось
   * бы там, где оно ничего не защищает.
   */
  useEffect(() => {
    setSearchOpen(false);
    setQuery("");
    setFound(null);
  }, [chat.id]);

  /**
   * Копирование текста сообщения.
   *
   * Через navigator.clipboard, а не через устаревший execCommand: последний
   * требует выделения в DOM и в ленте с виртуальной прокруткой ведёт себя
   * непредсказуемо. Отказ проглатываем молча — единственная его причина здесь
   * это запрет доступа к буферу, и сообщать о нём нечего.
   */
  const copyText = async (
    value: string,
    entities: TelegramTextEntityDto[] = [],
  ): Promise<void> => {
    // С премиальными эмодзи — двумя представлениями: HTML несёт
    // идентификаторы, без них вставка в заготовку давала обычный смайл.
    if (hasCustomEmoji(entities) && (await writeRichClipboard({ text: value, entities }))) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // navigator.clipboard отказывает при запрете разрешения и в
      // незащищённом контексте. Молча сдаваться нельзя: кнопка выглядела
      // нажатой, а текст в буфер не попадал.
    }

    /*
     * Запасной путь через скрытое поле и execCommand.
     *
     * Метод устаревший, но работает там, где отказал первый, и не требует
     * разрешений. Поле выключено из потока и не получает фокус визуально —
     * страница не дёргается.
     */
    try {
      const holder = document.createElement("textarea");
      holder.value = value;
      holder.setAttribute("readonly", "");
      holder.style.cssText = "position:fixed;top:-1000px;opacity:0";
      document.body.appendChild(holder);
      holder.select();
      document.execCommand("copy");
      holder.remove();
    } catch {
      setError("Не удалось скопировать: буфер обмена недоступен");
    }
  };

  const title = chatTitle(chat.title);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SaveAsTemplateDialog
        chatId={chat.id}
        message={templateFrom}
        onClose={() => setTemplateFrom(null)}
      />
      {/*
        Шапка чата собрана по телеграмной раскладке: аватар, название, под ним
        подзаголовок, справа — поиск по переписке и меню.
        Стекло оставлено: лента прокручивается ПОД шапкой, и переписка не
        обрывается ровной кромкой, как под непрозрачной планкой.
      */}
      {/*
        Шапка чата стала выше и тяжелее.
        Прежние 2 px вертикального отступа при аватаре 36 px давали полосу в
        44 px — тоньше строки списка чатов слева. Из-за этого главный экран
        начинался с самого лёгкого элемента, и взгляду не за что было
        зацепиться при переходе от списка к переписке.
      */}
      <header className={cn("flex flex-none items-center gap-2.5 border-b border-border/60 bg-card/85 px-3.5 py-2 shadow-[0_1px_0_0_rgba(2,5,32,0.04)] backdrop-blur-xl", onBack && "gap-2 pl-1")}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Назад к чатам"
            className="grid size-9 flex-none place-items-center rounded-full border-0 bg-transparent p-0 text-brand"
          >
            <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        <span
          className="relative flex flex-none"
          {...(onOpenDetails ? { role: "button", tabIndex: 0, onClick: onOpenDetails, "aria-label": "Карточка чата" } : {})}
        >
          <Avatar
            name={title}
            seed={chat.id}
            size="md"
            photoUrl={chat.hasPhoto ? `/api/chats/${chat.id}/photo` : null}
          />
          {/*
            Точка состояния подключения на аватаре собеседника. Дублирует
            индикатор у поля ввода намеренно: там он говорит «с чего уйдёт
            сообщение», здесь — «жив ли канал прямо сейчас». Взгляд при чтении
            переписки находится вверху, а не внизу.
          */}
          <span
            className={cn(
              "absolute -bottom-px -right-px size-3 rounded-full border-2 border-card",
              PRESENCE[chat.account.connectionState] ?? "bg-muted-foreground",
            )}
            title={`Аккаунт: ${chat.account.connectionState}`}
          />
        </span>

        <div
          className={cn("flex min-w-0 flex-1 flex-col leading-tight", onOpenDetails && "cursor-pointer")}
          {...(onOpenDetails ? { onClick: onOpenDetails } : {})}
        >
          <h2 className="truncate text-[15px] font-semibold text-heading">
            {title}
          </h2>
          {/*
            Подзаголовок как в Telegram: сперва то, что говорит о собеседнике,
            и только потом служебное. Метка аккаунта обязана остаться — §5 ТЗ
            требует, чтобы менеджер всегда видел, от чьего лица он в чате.
          */}
          <span className="truncate text-[13px]">
            {/* «Печатает» вытесняет статус: это и есть статус на ближайшие
                секунды, а два рядом — «в сети · печатает» — Telegram не
                показывает. */}
            {activity ? (
              <span className="font-medium text-brand">
                {activity}
                <TypingDots />
                {" · "}
              </span>
            ) : (
              presence?.status && (
                <span
                  className={
                    presence.online
                      ? "font-medium text-brand"
                      : "text-muted-foreground"
                  }
                >
                  {presence.status}
                  {" · "}
                </span>
              )
            )}
            {presence?.blocked && (
              <span className="font-medium text-destructive">
                заблокирован ·{" "}
              </span>
            )}
            <span className="text-muted-foreground">
              {[
                presence?.status || activity ? null : chatTypeBadge(chat.type),
                chat.peerUsername ? `@${chat.peerUsername}` : null,
                `через ${accountLabel(chat.account)}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        </div>

        {/*
          Поиск по переписке и меню чата — те же места, что в Telegram.
          Обе кнопки пока объявлены неактивными, а не спрятаны: пустое место
          там, где пользователь их ищет, читается как поломка, а неактивная
          кнопка с подписью говорит, чего именно ещё нет.
        */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Поиск по переписке"
              aria-pressed={searchOpen}
              onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
              className={cn(
                "flex-none",
                searchOpen ? "text-brand" : "text-muted-foreground",
              )}
            >
              <SearchIcon className="size-[18px]" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Поиск по переписке</TooltipContent>
        </Tooltip>
      </header>

      {/*
        Закреплённое сообщение — полосой под шапкой, как в Telegram.
        Оно всегда на виду и не требует прокрутки к себе: закрепляют ровно
        для того, чтобы не искать.
      */}
      {pinned && (
        <div className="flex flex-none items-center gap-2.5 border-b border-border/60 bg-card/85 px-4 py-2 backdrop-blur-xl">
          <span
            aria-hidden
            className="h-8 w-[3px] flex-none rounded-full bg-brand"
          />
          {/* Нажатие ведёт к самому сообщению — как в Telegram. Закрепляют
              ровно для того, чтобы к нему возвращаться. */}
          <button
            type="button"
            className="min-w-0 flex-1 rounded-lg border-transparent bg-transparent px-1 py-0.5 text-left transition-colors hover:bg-accent"
            onClick={() => jumpTo(pinned.id)}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand">
              Закреплённое
            </p>
            <p className="truncate text-[13px] text-muted-foreground">
              {messageSummary(pinned)}
            </p>
          </button>
          {/* «Все» открывает панель со всеми закрепами — Telegram показывает
              их отдельным списком, а не только верхний. */}
          <button
            type="button"
            className="flex-none rounded-lg px-2 py-1 text-[12px] font-medium text-brand transition-colors hover:bg-accent"
            onClick={() => void openPinnedList()}
          >
            Все
          </button>
          {canAct && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="flex-none text-muted-foreground"
              aria-label="Открепить"
              title="Открепить"
              onClick={() => void togglePin(pinned)}
            >
              <PinOffIcon className="size-[18px]" />
            </Button>
          )}
        </div>
      )}

      {/*
        Панель всех закреплённых. Telegram показывает их списком, а не только
        верхний; сюда попадают все, отсортированные свежими вниз (как в чате).
      */}
      {pinnedListOpen && (
        <div className="flex flex-none max-h-[40vh] flex-col border-b border-border/60 bg-card/95 backdrop-blur-xl">
          <div className="flex items-center justify-between px-4 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-brand">
              Закреплённые{allPinned ? ` · ${allPinned.length}` : ""}
            </p>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Закрыть"
              title="Закрыть"
              onClick={() => setPinnedListOpen(false)}
            >
              ×
            </Button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
            {allPinned === null && (
              <p className="px-2 py-3 text-[13px] text-muted-foreground">Загрузка…</p>
            )}
            {allPinned?.length === 0 && (
              <p className="px-2 py-3 text-[13px] text-muted-foreground">Закреплённых нет.</p>
            )}
            {allPinned?.map((m) => (
              <div key={m.id} className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-accent">
                <span aria-hidden className="mt-1 h-8 w-[3px] flex-none rounded-full bg-brand/70" />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setPinnedListOpen(false);
                    jumpTo(m.id);
                  }}
                >
                  <p className="line-clamp-2 whitespace-pre-wrap text-[13px] leading-relaxed">
                    {m.text?.trim() ? (
                      <RichText text={m.text} entities={m.entities} chatId={chat.id} />
                    ) : (
                      <span className="text-muted-foreground">{messageSummary(m)}</span>
                    )}
                  </p>
                </button>
                {canAct && (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="flex-none text-muted-foreground"
                    aria-label="Открепить"
                    title="Открепить"
                    onClick={() => {
                      void togglePin(m, false, true);
                      setAllPinned((prev) => prev?.filter((x) => x.id !== m.id) ?? prev);
                    }}
                  >
                    <PinOffIcon className="size-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {searchOpen && (
        <div className="flex flex-none flex-col gap-2 border-b border-border/60 bg-card/85 px-4 py-2.5 backdrop-blur-xl">
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void runSearch();
            }}
          >
            <div className="relative flex min-w-0 flex-1 items-center">
              <SearchIcon className="pointer-events-none absolute left-3 size-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    closeSearch();
                    return;
                  }
                  /*
                   * Enter обрабатывается явно, а не через неявную отправку
                   * формы. Неявная зависит от того, как именно пришло событие,
                   * и в части сред не срабатывает вовсе — а поиск, который
                   * запускается только кнопкой, в мессенджере ощущается
                   * сломанным.
                   */
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void runSearch();
                  }
                }}
                placeholder="Искать в этой переписке"
                className="h-9 w-full rounded-full border border-border bg-background pl-9 pr-3 text-sm outline-none transition-colors focus:border-ring"
              />
            </div>
            <Button
              size="sm"
              type="submit"
              disabled={searching || query.trim().length === 0}
            >
              {searching ? "Ищем…" : "Найти"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={closeSearch}
            >
              Отмена
            </Button>
          </form>

          {found !== null && (
            <div className="max-h-64 overflow-y-auto">
              {found.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted-foreground">
                  Ничего не найдено в этой переписке
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {found.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        className="w-full rounded-lg border-transparent bg-transparent px-2.5 py-1.5 text-left transition-colors hover:bg-accent"
                        onClick={() => jumpTo(m.id)}
                      >
                        <p className="line-clamp-2 text-[13px] leading-snug">
                          {messageSummary(m)}
                        </p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          {m.direction === "OUTBOUND"
                            ? "Исходящее"
                            : "Входящее"}
                          {m.sentAt && ` · ${formatDate(m.sentAt)}`}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={feedRef}
          className="chat-wallpaper flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-6 py-5"
        >
          {loadingOlder && (
            <p className="self-center rounded-full bg-card/80 px-3 py-1 text-[12px] text-muted-foreground shadow-sm">
              Загружаю более ранние сообщения…
            </p>
          )}

          {messages.length === 0 && (
            <p className="p-5 text-sm text-muted-foreground">
              Сообщений пока нет
            </p>
          )}

          {error && (
            <p
              role="alert"
              className="self-center rounded-full bg-destructive/15 px-3.5 py-1.5 text-sm text-destructive"
            >
              {error}
            </p>
          )}

          {messages.map((message, index) => {
            const previous = messages[index - 1] ?? null;
            const next = messages[index + 1] ?? null;

            const grouped = continues(previous, message);
            const tail = !continues(message, next);
            const showDate =
              !previous || !sameDay(previous.sentAt, message.sentAt);
            const deleted = message.deliveryState === "DELETED";
            const out = message.direction === "OUTBOUND";
            const picked = selectedIds.has(message.id);

            /*
             * Служебное сообщение — строка по центру, а не пузырь.
             *
             * Раньше «закрепил сообщение» приходило неизвестным типом и
             * показывалось пузырём с подписью «Вложение»: выглядело так, будто
             * закрепление отправило кому-то файл.
             */
            if (message.service) {
              return (
                <div
                  key={message.id}
                  className="my-1.5 self-center rounded-full bg-[var(--date-pill-bg)]/70 px-3 py-1 text-[12px] text-[var(--date-pill-text)]"
                >
                  {message.service}
                </div>
              );
            }

            return (
              <Fragment key={message.id}>
                {/*
                Граница непрочитанного. В Telegram она выглядит подписанной
                чертой во всю ширину, а не плашкой: черта отделяет, плашка
                привлекает внимание к себе.
              */}
                {message.id === firstUnreadId && (
                  <div className="my-2 flex items-center gap-3 self-stretch">
                    <span className="h-px flex-1 bg-[var(--date-pill-bg)]/35" />
                    <span className="text-[12px] font-medium text-[var(--bubble-time)]">
                      Непрочитанные
                    </span>
                    <span className="h-px flex-1 bg-[var(--date-pill-bg)]/35" />
                  </div>
                )}

                {showDate && (
                  <div className="sticky top-0 z-10 my-2 self-center rounded-full bg-[var(--date-pill-bg)] px-3 py-1 text-xs font-medium text-[var(--date-pill-text)] shadow-sm backdrop-blur">
                    {formatDate(message.sentAt)}
                  </div>
                )}

                {/*
                Строка сообщения: пузырь и кнопки действий — соседи, а не
                слои. Раньше кнопки лежали ПОВЕРХ пузыря и закрывали собой
                первую строку текста: чтобы дочитать сообщение, приходилось
                уводить курсор в сторону. Здесь они занимают своё место сбоку,
                а пузырь при нехватке ширины просто сужается.
              */}
                <div
                  className={cn(
                    "group flex w-full items-start gap-1",
                    out ? "flex-row-reverse justify-start" : "justify-start",
                  )}
                >
                  <article
                    data-message-id={message.id}
                    className={cn(
                      "bubble-enter relative min-w-[88px] max-w-[min(74%,540px)] rounded-[18px] px-3.5 py-2 shadow-[var(--bubble-shadow)]",
                      out
                        ? "bg-[var(--bubble-out)] text-[var(--bubble-out-text)]"
                        : "bg-[var(--bubble-in)] text-[var(--bubble-in-text)]",
                      // Группировка: подряд идущие сообщения одного отправителя
                      // прижимаются друг к другу, и только у последнего рисуется
                      // хвост. Такой ритм и делает ленту узнаваемо «телеграмной».
                      grouped && (out ? "rounded-tr-md" : "rounded-tl-md"),
                      tail && "mb-2 bubble-tail",
                      tail &&
                        (out
                          ? "rounded-br-[4px] bubble-tail-out"
                          : "rounded-bl-[4px]"),
                      deleted && "italic opacity-60",
                      picked &&
                        "ring-2 ring-primary ring-offset-2 ring-offset-[var(--chat-bg)]",
                      // Подсветка после перехода: короткая вспышка, чтобы глаз
                      // нашёл сообщение среди похожих.
                      highlighted === message.id &&
                        "ring-2 ring-brand ring-offset-2 ring-offset-[var(--chat-bg)]",
                    )}
                    /*
                     * Пока хоть одно сообщение выделено, нажатие по пузырю
                     * переключает выделение. Иначе набор обрывался бы на первом
                     * же случайном клике по ленте.
                     */
                    onClick={() => selecting && onToggleSelect(message.id)}
                  >
                    {/* Флажок появляется по наведению и остаётся у выделенных. */}
                    <span
                      role="checkbox"
                      aria-checked={picked}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSelect(message.id);
                      }}
                      className={cn(
                        "absolute -left-6 top-1/2 grid size-4 -translate-y-1/2 cursor-pointer place-items-center rounded-[5px] border transition-opacity",
                        picked
                          ? "border-transparent bg-primary text-primary-foreground opacity-100"
                          : "border-border bg-card opacity-0 group-hover:opacity-100",
                        selecting && "opacity-100",
                        out && "left-auto -right-6",
                      )}
                    >
                      {picked && <CheckIcon className="size-2.5" />}
                    </span>
                    {/* Автор известен только для отправленных через систему и
                    печатается один раз на группу, а не у каждого пузыря. */}
                    {message.sentBy && !grouped && (
                      <span
                        className={cn(
                          "mb-px block text-[12.5px] font-semibold",
                          out ? "text-[var(--bubble-time-out)]" : "text-brand",
                        )}
                      >
                        {message.sentBy.displayName}
                      </span>
                    )}

                    {editing === message.id ? (
                      <div className="flex min-w-[260px] flex-col gap-1.5">
                        <textarea
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              void saveEdit(message);
                            }
                            if (e.key === "Escape") setEditing(null);
                          }}
                          rows={2}
                          autoFocus
                          className="w-full resize-y rounded-lg border border-border bg-background p-2 text-sm outline-none focus:border-ring"
                        />
                        <div className="flex gap-1.5">
                          <Button
                            size="xs"
                            onClick={() => void saveEdit(message)}
                          >
                            Сохранить
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => setEditing(null)}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[15px] leading-relaxed">
                        {deleted ? (
                          <em className="pr-[54px]">Сообщение удалено</em>
                        ) : (
                          <>
                            {message.media && (
                              <MessageMedia
                                media={message.media}
                                chatId={chat.id}
                                messageId={telegramIdOf(message) ?? message.id}
                                outgoing={out}
                              />
                            )}
                            {/* Звонок — пузырь с трубкой, как в Telegram:
                                это не вложение, файла за ним нет. */}
                            {message.call && (
                              <CallCard call={message.call} outgoing={out} />
                            )}
                            {/*
                          Подпись к вложению рисуется обычным текстом: для
                          получателя это часть сообщения, а не свойство файла.
                          Отступ справа нужен только когда текст есть — иначе
                          под временем осталась бы пустая полоса.
                        */}
                            {message.text && (
                              <div className="whitespace-pre-wrap break-words pr-[54px]">
                                <RichText
                                  text={message.text}
                                  entities={message.entities}
                                  chatId={chat.id}
                                  onOpenPeer={onOpenPeer}
                                />
                              </div>
                            )}
                            {!message.text &&
                              !message.media &&
                              !message.call &&
                              message.hasMedia && (
                                <div className="pr-[54px] opacity-70">
                                  Вложение
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    )}

                    {/*
                  Время и галочки прижаты к нижнему правому углу и вписаны в
                  текст — как в Telegram. Отдельной строкой они съедали бы
                  высоту ленты.
                */}
                    <footer
                      className={cn(
                        "absolute bottom-1.5 right-2.5 flex items-center gap-1 text-[11px] leading-none",
                        out
                          ? "text-[var(--bubble-time-out)]"
                          : "text-[var(--bubble-time)]",
                      )}
                    >
                      {message.editedAt && <span className="italic">изм.</span>}
                      <span>{formatTime(message.sentAt)}</span>
                      {out && !deleted && (
                        <DeliveryTicks
                          state={message.deliveryState}
                          read={message.read === true}
                        />
                      )}
                    </footer>

                    {/*
                  Реакции под текстом пузыря, как в Telegram: своя выделена
                  заливкой. Нажатие по своей снимает её — то же поведение, что
                  в оригинале, и отдельной кнопки «убрать» не нужно.
                */}
                    {(message.reactions ?? []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(message.reactions ?? []).map((reaction) => (
                          <button
                            key={reaction.emoji}
                            type="button"
                            disabled={!canAct}
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleReaction(message, reaction.emoji);
                            }}
                            className={cn(
                              "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] transition-colors",
                              reaction.chosen
                                ? "border-brand bg-brand/15 text-brand"
                                : "border-transparent bg-black/5 text-[var(--bubble-time)] hover:bg-black/10",
                              !canAct && "cursor-default",
                            )}
                          >
                            <span aria-hidden>{reaction.emoji}</span>
                            <span className="tabular-nums">
                              {reaction.count}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/*
                  Действия открываются по наведению — постоянные кнопки шумят
                  в ленте.
                  Ответ и копирование доступны на ЛЮБОМ сообщении, включая
                  чужое: в Telegram это так же, и ограничивать их незачем.
                  Изменение и удаление — только на своих исходящих.
                */}
                  </article>

                  {!deleted && (
                    <span
                      className={cn(
                        "relative flex flex-none gap-0.5 self-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
                        // Открытая панель держит кнопки видимыми: иначе увод
                        // курсора к самой панели прятал бы её вместе с ними.
                        (reactAsk === message.id || pinAsk === message.id) &&
                          "opacity-100",
                      )}
                    >
                      {canReply && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label="Ответить"
                          title="Ответить"
                          onClick={() => onReply(message)}
                        >
                          <ReplyIcon />
                        </Button>
                      )}

                      {message.text && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label="Копировать текст"
                          title="Копировать текст"
                          onClick={() =>
                            void copyText(message.text as string, message.entities)
                          }
                        >
                          <CopyIcon />
                        </Button>
                      )}

                      {/*
                        В заготовки — для сообщений с текстом, фото или видео.
                        Только так в заготовку попадает премиальный эмодзи: с
                        клавиатуры его не набрать.
                      */}
                      {canManageTemplates &&
                        (message.text ||
                          message.media?.kind === "photo" ||
                          message.media?.kind === "video") && (
                          <Button
                            size="icon-xs"
                            variant="ghost"
                            aria-label="В заготовки"
                            title="Сохранить в заготовки"
                            onClick={() => setTemplateFrom(message)}
                          >
                            <BoltIcon />
                          </Button>
                        )}

                      {canAct && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label="Поставить реакцию"
                          title="Поставить реакцию"
                          onClick={() => {
                            setPinAsk(null);
                            setReactAsk((v) =>
                              v === message.id ? null : message.id,
                            );
                          }}
                        >
                          <span
                            aria-hidden
                            className="text-[13px] leading-none"
                          >
                            {QUICK_REACTION}
                          </span>
                        </Button>
                      )}

                      {canAct && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label={isPinned(message) ? "Открепить" : "Закрепить"}
                          title={isPinned(message) ? "Открепить" : "Закрепить"}
                          onClick={() => {
                            setReactAsk(null);
                            // Открепление однозначно, а вот закрепить можно
                            // для всех или только для себя — здесь спрашиваем.
                            if (isPinned(message))
                              void togglePin(message);
                            else
                              setPinAsk((v) =>
                                v === message.id ? null : message.id,
                              );
                          }}
                        >
                          {isPinned(message) ? <PinOffIcon /> : <PinIcon />}
                        </Button>
                      )}

                      {out && canEdit && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label="Изменить сообщение"
                          onClick={() => {
                            setEditing(message.id);
                            setDraft(message.text ?? "");
                          }}
                        >
                          <PencilIcon />
                        </Button>
                      )}
                      {/* Кнопка удаления скрыта у всех, кроме ADMIN. Это UX —
                        запрет держит сервер: прямой DELETE вернёт 403. */}
                      {out && canDelete && (
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          className="hover:bg-destructive/15 hover:text-destructive"
                          aria-label="Удалить сообщение"
                          onClick={() => void remove(message)}
                        >
                          <TrashIcon />
                        </Button>
                      )}
                      {reactAsk === message.id && (
                        <div
                          className={cn(
                            "absolute top-full z-30 mt-1 flex gap-0.5 rounded-full border border-border bg-popover p-1 shadow-lg",
                            out ? "left-0" : "right-0",
                          )}
                        >
                          {REACTIONS.map((emoji) => {
                            const chosen = (message.reactions ?? []).some(
                              (r) => r.emoji === emoji && r.chosen,
                            );
                            return (
                              <button
                                key={emoji}
                                type="button"
                                aria-label={`Реакция ${emoji}`}
                                className={cn(
                                  "flex h-7 w-7 items-center justify-center rounded-full border-transparent bg-transparent text-[16px] leading-none transition-transform hover:scale-125",
                                  chosen && "bg-brand/15",
                                )}
                                onClick={() =>
                                  void toggleReaction(message, emoji)
                                }
                              >
                                <span aria-hidden>{emoji}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {pinAsk === message.id && (
                        <div
                          className={cn(
                            "absolute top-full z-30 mt-1 w-56 rounded-xl border border-border bg-popover p-1 text-left shadow-lg",
                            out ? "left-0" : "right-0",
                          )}
                        >
                          <button
                            type="button"
                            className="w-full rounded-lg border-transparent bg-transparent px-2.5 py-1.5 text-left text-[13px] transition-colors hover:bg-accent"
                            onClick={() => void togglePin(message, false)}
                          >
                            Закрепить у всех
                            <span className="block text-[11px] text-muted-foreground">
                              Собеседник увидит закреплённое
                            </span>
                          </button>
                          <button
                            type="button"
                            className="w-full rounded-lg border-transparent bg-transparent px-2.5 py-1.5 text-left text-[13px] transition-colors hover:bg-accent"
                            onClick={() => void togglePin(message, true)}
                          >
                            Закрепить только у себя
                            <span className="block text-[11px] text-muted-foreground">
                              Пометка останется внутри компании
                            </span>
                          </button>
                        </div>
                      )}
                    </span>
                  )}
                </div>
              </Fragment>
            );
          })}

          <div ref={bottomRef} />
        </div>

        {/*
        Кнопка возврата к последнему сообщению.
        Появляется, только когда лента уведена вверх, — иначе она перекрывала
        бы переписку без всякой пользы. Счётчик показывает, сколько пришло,
        пока читали историю.
      */}
        {!atBottom && (
          <button
            type="button"
            onClick={scrollToBottom}
            aria-label="К последнему сообщению"
            title="К последнему сообщению"
            className="absolute bottom-4 right-5 z-20 grid size-11 place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-lg transition-colors hover:text-brand"
          >
            <ChevronIcon className="size-5" />
            {missed > 0 && (
              <span className="absolute -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
                {missed > 99 ? "99+" : missed}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Статус доставки галочками — так, как его читают в Telegram.
 *
 * PENDING рисуется часами осознанно: подтверждения от Telegram ещё не было, и
 * галочка здесь означала бы «доставлено», чего мы не знаем. Врать о статусе
 * отправки опаснее, чем показать неопределённость (та же логика, что и в
 * двухфазной отправке на стороне сервиса).
 */
function DeliveryTicks({
  state,
  read,
}: {
  state: TelegramMessageDto["deliveryState"];
  /** Прочитано собеседником — вторая галочка. */
  read: boolean;
}) {
  if (state === "PENDING") {
    return (
      <span
        className="inline-flex items-center"
        title="Ожидание подтверждения Telegram"
      >
        <ClockIcon className="size-[15px]" />
      </span>
    );
  }

  if (state === "FAILED") {
    return (
      <span
        className="inline-flex items-center font-semibold text-destructive"
        title="Сообщение не отправлено"
      >
        <AlertIcon className="size-[15px]" />
      </span>
    );
  }

  /*
   * Одна галочка — отправлено, две — прочитано собеседником. Как в
   * Telegram, и ровно поэтому раньше было неправильно: две галочки стояли
   * у всего отправленного, и менеджер считал, что клиент прочитал.
   */
  const edited = state === "EDITED" ? ", изменено" : "";
  if (read) {
    return (
      <span className="inline-flex items-center" title={`Прочитано${edited}`}>
        <ChecksIcon className="size-[15px]" />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center" title={`Отправлено${edited}`}>
      <CheckIcon className="size-[15px]" />
    </span>
  );
}

/**
 * Продолжает ли `b` группу, начатую `a`: тот же отправитель, то же направление
 * и разрыв меньше окна склейки.
 *
 * Сравнение по `sentBy?.id` важно: два менеджера, писавшие с одного аккаунта
 * подряд, — это разные авторы, и слипаться их сообщения не должны, иначе
 * подпись автора у второго потеряется.
 */
function continues(
  a: TelegramMessageDto | null,
  b: TelegramMessageDto | null,
): boolean {
  if (!a || !b) return false;
  if (a.direction !== b.direction) return false;
  if ((a.sentBy?.id ?? null) !== (b.sentBy?.id ?? null)) return false;
  if (!sameDay(a.sentAt, b.sentAt)) return false;

  if (!a.sentAt || !b.sentAt) return false;
  return (
    Math.abs(Date.parse(b.sentAt) - Date.parse(a.sentAt)) < GROUP_WINDOW_MS
  );
}

function sameDay(a: string | null, b: string | null): boolean {
  if (!a || !b) return a === b;
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** В пузыре — только часы и минуты: дата уже названа разделителем выше. */
function formatTime(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** «Сегодня» и «Вчера» читаются быстрее календарной даты. */
function formatDate(iso: string | null): string {
  if (!iso) return "";

  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Сегодня";
  if (date.toDateString() === yesterday.toDateString()) return "Вчера";

  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}
